import React, { useEffect, useState } from 'react';
import { InzaraEmbed } from '../inzara-embed-react';
import { Copy, ExternalLink, Loader2, Play, Zap } from 'lucide-react';

/**
 * Runtime SEO/OG tagging for the playground. INZARA's index.html carries
 * site-wide OG tags for the marketing homepage; hosting the playground
 * on the same SPA means Twitter/LinkedIn/Slack unfurls need per-route
 * overrides. Static crawlers won't see these (that requires SSR or a
 * prerender step — out of scope for now), but browsers that read the
 * runtime DOM (like the tab title, the AI-assistant "share this page"
 * flows, and some link-preview crawlers with headless browsers) will.
 */
function usePlaygroundSeo() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'INZARA Embed Playground — try the SDK live';

    // Update or insert an OG tag in the document head.
    const set = (attr, value, content) => {
      let el = document.head.querySelector(`meta[${attr}="${value}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const desc = 'Paste a workspace-scoped INZARA management key, pick a resource, and see the SDK render live. No signup.';
    const ogImage = `${window.location.origin}/embed-playground-og.png`;
    set('property', 'og:title',       'INZARA Embed Playground');
    set('property', 'og:description', desc);
    set('property', 'og:url',         window.location.href);
    set('property', 'og:image',       ogImage);
    set('property', 'og:image:width', '1200');
    set('property', 'og:image:height','630');
    set('property', 'og:image:alt',   'INZARA Embed Playground preview card showing SDK badges and KPI chips');
    set('name',     'description',    desc);
    set('name',     'twitter:card',        'summary_large_image');
    set('name',     'twitter:title',       'INZARA Embed Playground');
    set('name',     'twitter:description', desc);
    set('name',     'twitter:image',       ogImage);

    return () => { document.title = prevTitle; };
  }, []);
}

/**
 * EmbedPlaygroundPage — a public, no-signup landing route where any
 * developer can paste an INZARA management API key + a workspace ID,
 * mint an embed session in-place, and see the SDK-driven iframe render
 * live. Also shows the equivalent Vanilla-JS / Vue / Angular snippets
 * so hosts can port between them in seconds.
 *
 * Route: /embed-playground (no auth). We deliberately DO NOT persist
 * the mgmt key — the input is `type="password"` and never leaves the
 * browser except in the direct call to /api/mgmt/v1/embed/sessions.
 */

const RESOURCE_CHOICES = [
  { value: 'insights',     label: 'Insights',      needsId: false },
  { value: 'kpi-monitor',  label: 'KPI Monitor',   needsId: false },
  { value: 'dashboard',    label: 'Dashboard',     needsId: true  },
  { value: 'report',       label: 'Report',        needsId: true  },
];

function snippetReact({ host, resource, id, needsId }) {
  return `import { InzaraEmbed } from '@inzara/embed-react';

<InzaraEmbed
  host="${host}"
  token={jwtFromYourBackend}
  resource="${resource}"${needsId ? `\n  id="${id || '<id>'}"` : ''}
  onEvent={(ev) => console.log(ev)}
  onTokenRefresh={(next) => saveForNextMount(next)}
/>`;
}

function snippetVanilla({ host, resource, id, needsId }) {
  return `import { mount, RESOURCES } from '@inzara/embed-js';

const embed = mount(document.getElementById('inzara'), {
  host: '${host}',
  token: jwtFromYourBackend,
  resource: RESOURCES.${resource.toUpperCase().replace('-', '_')},${needsId ? `\n  id: '${id || '<id>'}',` : ''}
  onEvent: (ev) => console.log(ev),
});`;
}

function snippetCurl({ host, workspaceId, sub, resource, id, needsId }) {
  return `# Host backend mints the JWT with a workspace-scoped mgmt key
curl -X POST ${host}/api/mgmt/v1/embed/sessions \\
  -H "Authorization: Bearer $INZARA_MGMT_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify({
    workspace_id: workspaceId || '<workspace_id>',
    sub: sub || 'end-user@example.com',
    resource,
    ...(needsId && id ? { resource_id: id } : {}),
    ttl_seconds: 900,
  })}'`;
}

/**
 * Parse the query-string prefill on `/embed-playground?...`. Supported
 * keys: `workspace`, `resource`, `id`, `sub`. Pure function so it can
 * be unit-tested outside the DOM.
 *
 * Deliberately does NOT accept a `key=` parameter — mgmt keys are
 * secrets and would leak via referrer/history/access logs the moment
 * they landed in a URL. If we ever want key deep-links, we'll ship a
 * short-lived one-time-code exchange instead.
 */
export function parsePlaygroundPrefill(search) {
  const params = new URLSearchParams(search || '');
  const resource = params.get('resource') || '';
  const allowed  = new Set(['insights', 'kpi-monitor', 'dashboard', 'report']);
  return {
    workspace: (params.get('workspace') || params.get('ws') || '').trim(),
    resource:  allowed.has(resource) ? resource : '',
    id:        (params.get('id') || params.get('resource_id') || '').trim(),
    sub:       (params.get('sub') || '').trim(),
  };
}

export default function EmbedPlaygroundPage() {
  usePlaygroundSeo();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const prefill = typeof window !== 'undefined'
    ? parsePlaygroundPrefill(window.location.search)
    : { workspace: '', resource: '', id: '', sub: '' };

  const [mgmtKey,      setMgmtKey]      = useState('');
  const [workspaceId,  setWorkspaceId]  = useState(prefill.workspace);
  const [resource,     setResource]     = useState(prefill.resource || 'kpi-monitor');
  const [resourceId,   setResourceId]   = useState(prefill.id);
  const [subject,      setSubject]      = useState(prefill.sub || 'playground@example.com');
  const [minting,      setMinting]      = useState(false);
  const [error,        setError]        = useState(null);
  const [token,        setToken]        = useState('');
  const [expiresAt,    setExpiresAt]    = useState(null);
  const [snippetTab,   setSnippetTab]   = useState('react');
  const [refreshCount, setRefreshCount] = useState(0);
  const [heartbeat,    setHeartbeat]    = useState(null);
  const [heartbeatErr, setHeartbeatErr] = useState(null);
  const [prefillNotice, setPrefillNotice] = useState(
    prefill.workspace || prefill.resource || prefill.id || prefill.sub ? true : false,
  );

  // Defense in depth: if a caller ever accidentally puts a `key=` or
  // `mgmt_key=` in the URL, scrub it from the address bar the moment we
  // mount so it stops leaking via referrer/history. Screen-only — the
  // actual param is never read by this page.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const p = new URLSearchParams(window.location.search);
    let dirty = false;
    for (const bad of ['key', 'mgmt_key', 'api_key', 'token', 'secret']) {
      if (p.has(bad)) { p.delete(bad); dirty = true; }
    }
    if (dirty) {
      const clean = `${window.location.pathname}${p.toString() ? `?${p.toString()}` : ''}${window.location.hash}`;
      window.history.replaceState({}, document.title, clean);
    }
  }, []);

  async function heartbeatPing() {
    setHeartbeat(null); setHeartbeatErr(null);
    if (!mgmtKey.trim()) { setHeartbeatErr('Paste a mgmt key first'); return; }
    try {
      const r = await fetch(`${backendUrl}/api/mgmt/v1/status`, {
        headers: { 'Authorization': `Bearer ${mgmtKey.trim()}` },
      });
      const rl = {
        limit:     r.headers.get('x-ratelimit-limit'),
        remaining: r.headers.get('x-ratelimit-remaining'),
        reset:     r.headers.get('x-ratelimit-reset'),
      };
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b?.detail?.message || `HTTP ${r.status}`);
      }
      const body = await r.json();
      setHeartbeat({ ...body, rate_limit: rl });
    } catch (e) {
      setHeartbeatErr(String(e?.message || e));
    }
  }

  const meta = RESOURCE_CHOICES.find(r => r.value === resource);
  const canMint = mgmtKey.trim() && workspaceId.trim() && (!meta.needsId || resourceId.trim());

  async function mint() {
    if (!canMint) return;
    setMinting(true); setError(null); setToken(''); setRefreshCount(0);
    try {
      const res = await fetch(`${backendUrl}/api/mgmt/v1/embed/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${mgmtKey.trim()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: workspaceId.trim(),
          sub: subject.trim() || 'playground@example.com',
          roles: ['viewer'],
          resource: resource.replace('-', '_'),
          resource_id: meta.needsId ? resourceId.trim() : null,
          capabilities: ['read'],
          ttl_seconds: 300,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.message || b?.detail || `HTTP ${res.status}`);
      }
      const body = await res.json();
      setToken(body.token);
      setExpiresAt(body.expires_at);
    } catch (e) {
      setError(String(e?.message || e));
    } finally { setMinting(false); }
  }

  function copy(text) {
    try { navigator.clipboard.writeText(text); } catch { /* ignore */ }
  }

  const snippets = {
    react:   snippetReact({ host: backendUrl, resource, id: resourceId, needsId: meta.needsId }),
    vanilla: snippetVanilla({ host: backendUrl, resource, id: resourceId, needsId: meta.needsId }),
    curl:    snippetCurl({ host: backendUrl, workspaceId, sub: subject, resource: resource.replace('-', '_'), id: resourceId, needsId: meta.needsId }),
  };

  return (
    <div className="min-h-screen bg-[#0b0b0f] text-slate-100 p-6" data-testid="embed-playground-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <header className="flex items-center gap-3">
          <Zap className="w-6 h-6 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-semibold" data-testid="playground-title">INZARA Embed Playground</h1>
            <p className="text-sm text-slate-400 mt-1">
              Paste a workspace-scoped management API key, pick a resource, and see the SDK render live.
              Your key is <b>never persisted</b> — it&apos;s used once against
              <code className="mx-1 px-1 py-0.5 bg-slate-800/60 rounded text-[11px]">/api/mgmt/v1/embed/sessions</code>
              to mint a short-lived viewer JWT.
            </p>
          </div>
        </header>

        <section className="rounded-lg border border-slate-800/70 bg-slate-950/50 p-4">
          {prefillNotice && (
            <div
              className="mb-3 flex items-center justify-between gap-2 rounded border border-indigo-500/40 bg-indigo-500/10 px-3 py-1.5 text-[11px] text-indigo-200"
              data-testid="playground-prefill-notice"
            >
              <span>Prefilled from URL — paste your mgmt key and hit Mint to go live.</span>
              <button
                type="button"
                onClick={() => setPrefillNotice(false)}
                className="opacity-70 hover:opacity-100"
                data-testid="playground-prefill-dismiss"
              >
                ×
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <label className="space-y-1 md:col-span-2">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">
                Management API key (workspace-scoped, sessions:issue)
              </span>
              <input
                type="password"
                value={mgmtKey}
                onChange={(e) => setMgmtKey(e.target.value)}
                placeholder="sk_test_…"
                spellCheck={false}
                autoComplete="off"
                className="w-full font-mono px-2 py-1.5 rounded border border-slate-700 bg-slate-900 focus:border-indigo-500 outline-none"
                data-testid="playground-mgmt-key"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Workspace id</span>
              <input
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
                placeholder="ws_…"
                spellCheck={false}
                className="w-full font-mono px-2 py-1.5 rounded border border-slate-700 bg-slate-900 focus:border-indigo-500 outline-none"
                data-testid="playground-workspace-id"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Viewer identity</span>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="user@example.com"
                spellCheck={false}
                className="w-full px-2 py-1.5 rounded border border-slate-700 bg-slate-900 focus:border-indigo-500 outline-none"
                data-testid="playground-sub"
              />
            </label>
            <label className="space-y-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Resource</span>
              <select
                value={resource}
                onChange={(e) => setResource(e.target.value)}
                className="w-full px-2 py-1.5 rounded border border-slate-700 bg-slate-900 focus:border-indigo-500 outline-none"
                data-testid="playground-resource"
              >
                {RESOURCE_CHOICES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
            <label className={`space-y-1 ${meta.needsId ? '' : 'opacity-40'}`}>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Resource id</span>
              <input
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                placeholder={meta.needsId ? 'dash_… / rpt_…' : 'not required'}
                disabled={!meta.needsId}
                spellCheck={false}
                className="w-full font-mono px-2 py-1.5 rounded border border-slate-700 bg-slate-900 focus:border-indigo-500 outline-none"
                data-testid="playground-resource-id"
              />
            </label>
          </div>

          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={mint}
              disabled={!canMint || minting}
              className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="playground-mint"
            >
              {minting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              {minting ? 'Minting…' : token ? 'Re-mint & reload' : 'Mint session & render'}
            </button>
            <button
              type="button"
              onClick={heartbeatPing}
              disabled={!mgmtKey.trim()}
              className="text-xs px-3 py-1.5 rounded border border-slate-700 hover:border-slate-500 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="playground-heartbeat"
            >
              Heartbeat
            </button>
            {error && (
              <span className="text-xs text-rose-400" data-testid="playground-error">
                {error}
              </span>
            )}
            {token && !error && (
              <span className="text-[10px] text-slate-500">
                Token expires {expiresAt ? new Date(expiresAt).toLocaleTimeString() : ''} · auto-refresh will rotate silently ×{refreshCount}
              </span>
            )}
          </div>

          {(heartbeat || heartbeatErr) && (
            <div
              className="mt-3 text-[11px] rounded border border-slate-800 bg-black/40 p-2"
              data-testid="playground-heartbeat-result"
            >
              {heartbeatErr && (
                <span className="text-rose-400">Heartbeat failed: {heartbeatErr}</span>
              )}
              {heartbeat && (
                <div className="flex flex-wrap gap-3 text-slate-300 font-mono">
                  <span>tenant=<b>{heartbeat.tenant_id}</b></span>
                  <span>key=<b>{heartbeat.key_name}</b> ({heartbeat.env})</span>
                  <span>scopes=<b>{(heartbeat.scopes || []).join(', ') || '—'}</b></span>
                  <span>workspaces=<b>{heartbeat.workspace_count}</b></span>
                  {heartbeat.rate_limit?.remaining && (
                    <span>quota=<b>{heartbeat.rate_limit.remaining}/{heartbeat.rate_limit.limit}</b></span>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {token && (
          <section
            className="rounded-lg border border-slate-800/70 overflow-hidden bg-black/40"
            data-testid="playground-embed-frame"
          >
            <InzaraEmbed
              host={backendUrl}
              token={token}
              resource={resource}
              id={meta.needsId ? resourceId : undefined}
              height={560}
              onTokenRefresh={() => setRefreshCount(c => c + 1)}
            />
          </section>
        )}

        <section className="rounded-lg border border-slate-800/70 bg-slate-950/50 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-slate-500">Copy this into your app</div>
            <div className="flex gap-1 text-[11px]">
              {['react', 'vanilla', 'curl'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setSnippetTab(t)}
                  className={`px-2 py-1 rounded ${snippetTab === t ? 'bg-indigo-600 text-white' : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'}`}
                  data-testid={`playground-tab-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="relative">
            <pre
              className="text-[11px] font-mono bg-black/40 rounded p-3 overflow-x-auto whitespace-pre"
              data-testid={`playground-snippet-${snippetTab}`}
            >
              {snippets[snippetTab]}
            </pre>
            <button
              type="button"
              onClick={() => copy(snippets[snippetTab])}
              className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-800/80 hover:bg-slate-700 inline-flex items-center gap-1"
              data-testid="playground-copy"
              aria-label="Copy snippet"
            >
              <Copy className="w-3 h-3" /> copy
            </button>
          </div>
        </section>

        <footer className="text-[11px] text-slate-500 flex items-center gap-3">
          <ExternalLink className="w-3 h-3" />
          Frozen OpenAPI:
          <a
            href={`${backendUrl}/api/mgmt/v1/openapi.json`}
            target="_blank"
            rel="noreferrer"
            className="text-indigo-400 hover:text-indigo-300 underline"
            data-testid="playground-openapi-link"
          >
            /api/mgmt/v1/openapi.json
          </a>
          <span className="mx-2">·</span>
          Docs at
          <a
            href="/developers"
            className="text-indigo-400 hover:text-indigo-300 underline"
            data-testid="playground-dev-portal-link"
          >
            /developers
          </a>
        </footer>
      </div>
    </div>
  );
}
